import type { Good } from "@/entities/good";
import { patchGoodGenerationMarker } from "@/api/saveGood";
import { useGoodsStore } from "@/stores/goods";
import { useToastStore } from "@/stores/toast";
import { useContentTabsIndicators } from "@/stores/contentTabsIndicators";
import { resolveGoodContentTab } from "@/utils/goodContentTab";

/** Снимает в pinia маркер «генерируется» с товара (без сети). */
function clearGeneratingInStore(goodId: string): void {
  const goodsStore = useGoodsStore();
  const good = (goodsStore.goods as Good[]).find((g) => g.id === goodId);
  if (!good) return;
  good.generating = false;
  good.contentJobId = undefined;
  good.contentJobOwnerId = undefined;
  good.generationContext = undefined;
}

/**
 * Доводит товар до завершённого состояния после успешной генерации content-job'а.
 * Используется И мастером (`finalizeContentPipelineSuccess`), И фоновым вотчером
 * (`contentJobWatcher`) — поэтому ВНЕ его остаётся биллинг/статистика (это забота
 * мастера). Здесь только: тихий refresh (подтянуть сохранённую бэкендом версию),
 * снятие маркера в pinia + БД и единоразовый тост.
 *
 * Идемпотентно: тост показывается, только если товар ещё был помечен `generating`.
 */
export async function markGoodGenerationComplete(
  goodId: string,
  options?: { toast?: boolean },
): Promise<void> {
  const goodsStore = useGoodsStore();
  const before = (goodsStore.goods as Good[]).find((g) => g.id === goodId);
  const wasGenerating = before?.generating === true;
  const completedTab = before ? resolveGoodContentTab(before) : null;

  // Снять маркер в БД (best-effort; пайплайн уже мог его снять — это идемпотентно).
  try {
    await patchGoodGenerationMarker(goodId, {
      generating: false,
      contentJobId: null,
      contentJobOwnerId: null,
      generationContext: null,
    });
  } catch (error) {
    console.warn("[goodGenerationCompletion] failed to clear marker in DB", error);
  }

  // Подтянуть свежие версии (бэкенд уже сохранил сгенерированную версию).
  try {
    await goodsStore.refresh({ silent: true });
  } catch (error) {
    console.warn("[goodGenerationCompletion] silent refresh failed", error);
  }

  // На случай, если refresh вернул товар ещё с маркером (рассинхрон) — гасим локально.
  clearGeneratingInStore(goodId);

  if (wasGenerating && options?.toast !== false) {
    useToastStore().push({
      title: "Контент сгенерирован",
      tone: "success",
      state: "done",
    });
  }

  // Если генерация завершилась в неактивной вкладке — зажигаем «непрочитанную» точку.
  if (wasGenerating && completedTab) {
    useContentTabsIndicators().markCompleted(completedTab);
  }
}

/** Завершение по ошибке: снимаем маркер, показываем тост об ошибке. */
export async function markGoodGenerationFailed(
  goodId: string,
  message?: string,
): Promise<void> {
  try {
    await patchGoodGenerationMarker(goodId, {
      generating: false,
      contentJobId: null,
      contentJobOwnerId: null,
      generationContext: null,
    });
  } catch (error) {
    console.warn("[goodGenerationCompletion] failed to clear marker in DB", error);
  }
  clearGeneratingInStore(goodId);
  try {
    await useGoodsStore().refresh({ silent: true });
  } catch {
    // noop
  }
  useToastStore().push({
    title: "Не удалось сгенерировать контент",
    description: message,
    tone: "error",
    state: "done",
  });
}
